import { notifyCacheChange } from "./changes";
import { getCache, isSignedIn } from "./init";

export type CacheItem<T> = {
  readonly key: string;
  /** The cached value, or a fresh fetch. Concurrent callers share one fetch. */
  get: (forceRefresh?: boolean) => Promise<T>;
  /** The cached value without fetching, null when missing or expired. */
  peek: () => T | null;
  set: (value: T) => void;
  remove: () => void;
};

type CacheItemOptions<T> = {
  key: string;
  /** Milliseconds; no expiry when absent. */
  ttl?: number;
  supplier: () => Promise<T>;
  /** Answered, and not cached, when signed out or when the supplier fails. */
  fallback: () => T;
  /** Skip the API entirely when signed out. Default true. */
  requiresSignIn?: boolean;
};

const fetching = new Map<string, Promise<unknown>>();

const load = async <T>(options: CacheItemOptions<T>, store: (value: T) => void): Promise<T> => {
  if (options.requiresSignIn !== false && !(await isSignedIn())) {
    return options.fallback();
  }
  try {
    const value = await options.supplier();
    // A sign-out that landed while this was in flight must not get the old user's data written back into the cache.
    if (options.requiresSignIn !== false && !(await isSignedIn())) {
      return options.fallback();
    }
    store(value);
    return value;
  } catch (error) {
    console.error(`Failed to fetch ${options.key}:`, error);
    return options.fallback();
  }
};

export const cacheItem = <T>(options: CacheItemOptions<T>): CacheItem<T> => {
  const { key, ttl } = options;
  const peek = (): T | null => getCache().get<T>(key);
  const set = (value: T): void => {
    getCache().set(key, value, ttl === undefined ? undefined : Date.now() + ttl);
    notifyCacheChange(key);
  };
  const get = async (forceRefresh = false): Promise<T> => {
    const cached = forceRefresh ? null : peek();
    if (cached !== null) {
      return cached;
    }
    const pending = fetching.get(key) as Promise<T> | undefined;
    if (pending) {
      return pending;
    }
    const promise = load(options, set).finally(() => fetching.delete(key));
    fetching.set(key, promise);
    return promise;
  };
  const remove = (): void => {
    getCache().remove(key);
    notifyCacheChange(key);
  };
  return { key, get, peek, set, remove };
};
